/** Explicit, anonymous events via PostHog's public batch API. No autocapture or replay. */
import { safeLocal, safeSession } from "./safeStorage";
import { ANALYTICS_SESSION_KEY, STEALTH_KEY } from "./storageKeys";
import { getStoredUtm } from "./utm";

export const usesPostHog = () => process.env.REACT_APP_ANALYTICS_PROVIDER === "posthog";
const hosts = new Set(["https://us.i.posthog.com", "https://eu.i.posthog.com"]);
const allowedProps = new Set([
  "plan", "package_id", "campaign", "via", "source", "step", "step_index",
  "step_count", "steps", "count", "duration_ms", "duration_seconds", "dwell_seconds",
  "completed", "success", "is_guest", "is_pro", "locale", "device_type",
  "quadrant", "variant", "placement", "button", "category", "demo_mode",
  "method",
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
]);
let queue = [];
let timer;
let listening = false;

const config = () => {
  const key = process.env.REACT_APP_POSTHOG_KEY || "";
  const host = (process.env.REACT_APP_POSTHOG_HOST || "").replace(/\/$/, "");
  return key.startsWith("phc_") && hosts.has(host) ? { key, host } : null;
};

const excluded = () => typeof window === "undefined" ||
  safeLocal.getItem("trubeaute.consent.analytics") !== "granted" ||
  safeLocal.getItem(STEALTH_KEY) === "1" ||
  /^\/demo(?:\/|$)/.test(window.location.pathname) ||
  !!window.__trubeaute_demo_mode;

export const clearPostHogQueue = () => {
  queue = [];
  clearTimeout(timer);
  timer = undefined;
};

// Dynamic routes must not export routine, account, or share identifiers.
const safeRoute = () => {
  const path = window.location.pathname;
  const dynamic = path.match(/^\/(routine|techniques|lessons|guides)\/[^/]+\/?$/);
  if (dynamic) return `/${dynamic[1]}/:id`;
  // Only known static routes leave the browser; arbitrary 404 URLs can contain PII.
  const known = new Set([
    "/", "/start", "/sample-routine", "/home", "/arsenal", "/login",
    "/onboarding", "/capture", "/analyzing", "/my-routines", "/techniques",
    "/tutorials/library", "/pricing", "/pitch", "/lock-in", "/lock-in/success",
    "/pride", "/pride/success", "/yc", "/yc/claim", "/recreate", "/settings",
    "/auth/google", "/auth/magic-link", "/auth/forgot-password", "/auth/reset-password",
    "/auth/verify-email", "/billing/success", "/ambassador", "/ambassador/claim",
    "/pro/claim", "/ambassadors", "/ambassadors/apply", "/trouve/chat", "/my-trouve-chats",
    "/about", "/resources", "/privacy", "/terms", "/contact", "/partners", "/enterprise",
    "/support", "/lessons", "/guides", "/editorial-standards", "/recommendation-methodology",
    "/privacy/image-processing", "/corrections", "/affiliate-disclosure", "/authors/iyal-basen",
    "/live-coach-demo", "/live-coach-demo/brows", "/ar-demo",
  ]);
  return known.has(path) ? path : "/other";
};

const safeProperties = (props) => Object.fromEntries(
  Object.entries(props || {}).filter(([key, value]) => allowedProps.has(key) &&
    (typeof value === "boolean" || (typeof value === "number" && Number.isFinite(value)) ||
      (typeof value === "string" && /^[a-zA-Z0-9 _.:/-]{1,80}$/.test(value) && !value.includes("://")))),
);

export const flushPostHog = () => {
  const settings = config();
  const batch = queue;
  clearPostHogQueue();
  if (!usesPostHog() || !settings || excluded() || !batch.length) return;
  try {
    // text/plain avoids a CORS preflight; the body is PostHog's JSON batch format.
    // No retry or backend fallback: delivery failure cannot reintroduce app load.
    window.fetch(`${settings.host}/batch/`, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      credentials: "omit",
      referrerPolicy: "no-referrer",
      keepalive: true,
      body: JSON.stringify({ api_key: settings.key, batch }),
    }).catch(() => {});
  } catch (_) { /* Analytics must never affect the product. */ }
};

export const capturePostHog = (event, props = {}, source = "first_party_backend") => {
  try {
    if (!usesPostHog() || !config() || excluded() || typeof event !== "string" || !/^[a-z][a-z0-9_]{0,99}$/.test(event)) return;
    let session = safeSession.getItem(ANALYTICS_SESSION_KEY);
    if (!session) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      session = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      safeSession.setItem(ANALYTICS_SESSION_KEY, session);
    }
    const properties = {
      ...safeProperties({ ...getStoredUtm(), ...props }),
      route: safeRoute(),
      source,
      distinct_id: session,
      session_id: session,
      $process_person_profile: false,
      $geoip_disable: true,
      $ip: null,
    };
    // Some existing actions call both track() and trackFP(). Keep the canonical
    // first-party event once within a batch, without suppressing same-stream repeats.
    const duplicate = queue.find((item) => item.event === event &&
      item.properties.distinct_id === session && item.properties.route === properties.route &&
      item.properties.source !== source);
    if (duplicate) {
      if (source === "first_party_backend") duplicate.properties = properties;
      return;
    }
    queue.push({ event, properties, timestamp: new Date().toISOString() });
    if (!listening) {
      window.addEventListener("pagehide", flushPostHog);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flushPostHog();
      });
      listening = true;
    }
    if (queue.length >= 10) flushPostHog();
    else if (!timer) timer = setTimeout(flushPostHog, 3000);
  } catch (_) { /* Storage or network restrictions must not break navigation. */ }
};
